// MACHINERY V2 (Stroje) — V4 shop-floor wall: the visible way out.
// Owner ruling 2026-08-24, after testing the shipped wall on .cc.
//
// ============================================================
//  WHY A VISIBLE CONTROL EXISTS ON A SURFACE THAT REFUSED ONE
// ============================================================
// M2.14 shipped the wall with an INVISIBLE exit only: Esc, taught by a chip
// that faded in on pointer movement. The owner ruled that the wall must also
// carry a persistent X in the top-right corner, "just like we have in the
// Settings menu" — always visible, never pointer-revealed.
//
// That supersedes the kiosk-purity half of the earlier door ruling. The rule is
// now "NO INTERACTIONS ON THE DATA SURFACE": every machine tile is still
// completely inert (WallTile.tsx's header), and THIS control — header chrome,
// about the page rather than about any machine — is sanctioned by the ruling.
// It is the only focusable node the wall renders.
//
// The pointer-revealed chip it replaces was DELETED, not kept beside it. The
// chip's entire purpose was discoverability; a persistent glyph serves that
// strictly better, and serves it to the first-time user who has not yet moved a
// mouse to summon anything. Two exit affordances on a surface designed to be
// glanced at from across a hall is one too many. Esc still works and is now an
// undocumented power-user shortcut (FleetWallView.tsx's keydown effect).
//
// ============================================================
//  THE SETTINGS IDIOM, AT A 44px HIT TARGET, AT EVERY WIDTH
// ============================================================
// The idiom being matched is SettingsV2DialogShell.tsx:226-235 (personally
// re-read at commit time): a `<button type="button" className="btn btn-ghost">`
// holding the `×` CHARACTER — not an SVG — at `{ width: 30, padding: 0,
// fontSize: 15, color: "var(--muted)" }`.
//
// `.btn` and `.btn-ghost` are ALREADY IN SCOPE here: they are defined under
// `.machinery-v2-root` at machinery-v2-tokens.scss:165-186 and :196-201, and
// MachineryV2Layout.tsx puts that class on the element wrapping the <Outlet/>
// this wall renders through (all three personally re-read at commit time).
// Reusing them is also the only way to get hover at all — a `:hover` rule
// cannot live in an inline style, which is precisely why the token sheet
// carries these atoms (its own note at :162-164).
//
// >>> THE 30px VISUAL AND THE 44px HIT TARGET ARE THE SAME CONTROL, AT BOTH
// TIERS — BY TWO DIFFERENT MECHANISMS, AND THE SPLIT IS FORCED. <<<
// The manager requires a >=44x44 target (the desktop-density exemption covered
// artboard-final controls; this one is ours, not the artboard's). The Settings
// idiom paints 30x30. Simply growing the button to 44 would satisfy the target
// but paint a 44x44 ghost pill on hover — visibly heavier than the control the
// owner pointed at. So the painted box is 30x30 at every width, and the target
// is reached without it growing:
//
//   >= 1280 — TODAY'S ELEMENT, BYTE FOR BYTE. One `.btn btn-ghost` button,
//     painted 30x30, with a transparent absolutely-positioned child spanning
//     `HIT_INSET` past every edge. `.btn:hover` and `.btn:focus-visible` are
//     the button's own, unchanged. THE INSET IS MEASURED FROM 28, NOT FROM 30:
//     the naive 30 + 7 + 7 = 44 is wrong by 2px (the -7 an earlier pass wrote
//     produced a 42px target). Three token-sheet rules, each opened and re-read
//     at commit time, set the real number:
//       :50   `.machinery-v2-root * { box-sizing: border-box; }` — so the
//             inline `width: 30` / `height: 30` below is a BORDER-box value.
//       :169  `.btn { border: 1px solid var(--border); }` — 1px on all four
//             edges.
//       :198  `.btn-ghost { border-color: transparent; }` — that RECOLOURS the
//             border. It does not remove it. The width stays 1px.
//     An absolutely positioned child resolves its insets against the nearest
//     positioned ancestor's PADDING box, and a 30px border box minus 1px of
//     border per side, at `padding: 0`, is a 28x28 padding box. So `inset:
//     -8px` spans 28 + 8 + 8 = 44 in both axes, and -7 spans 42.
//     `boxSizing: "content-box"` would make the naive reading true, but it
//     would also PAINT a 32x32 box and visibly diverge from the Settings X the
//     owner pointed at, so it is deliberately NOT used.
//
//   < 1280 — THE MODULE'S SHARED WRAPPER IDIOM (M2.16e). The button carries
//     `.mv2-x-hit` instead of `.btn`, is transparent and IS the 44x44 target,
//     and the artboard's 30x30 paint is a `<span>` child of it — the same
//     shape mobile/TouchChip.tsx, profile/tabs/partsAtoms.tsx and
//     specSheetToolbar's `Segment` use, and the same three lines of markup
//     mobile/TouchCloseX.tsx renders for TabDialog's and
//     readinessPopupSections' identical ✕. Its paint, hover and focus ring all
//     come from mobile/machinery-v2-touch.scss; read that block before
//     touching either branch. This file does NOT consume TouchCloseX, and that
//     component's header records why: only the wall owes 44px at BOTH tiers,
//     so only the wall has a bespoke desktop branch.
//
// WHY THE BRANCH EXISTS AT ALL, rather than one wrapper at both tiers: the
// module-wide `.btn` floor (same sheet) has NO opt-out since M2.16e, so below
// 1280 the paint cannot stay on a `.btn` — and at >= 1280 the paint cannot
// LEAVE `.btn`, because `.btn:focus-visible` is where this button's ring comes
// from and the desktop DOM must not change. Two tiers, one painted result.
//
// PLACEMENT, AND WHY THE OUTER BOX IS 30 IN BOTH BRANCHES AT THE PINNED HOST.
// WallHeader (>= 1280 only — FleetWallView picks it on `!V2_BELOW_DESKTOP`)
// pins this button absolutely to the header's top-right corner and centres a
// 30px box against `right: 28`; a 44px outer box would have moved the painted X
// 7px inboard, which the owner ruling does not allow. That is the desktop
// branch, and its outer box is still exactly 30x30. Below 1280 the host is
// WallMobileHeader, where the X is an ordinary last flex child with no pin, so
// the wrapper is free to BE the 44px box.
//
// NOT BROWSER-MEASURED: this environment runs no dev server (production build
// only), so 44 is the static derivation above, not an observed DevTools box.
//
// The desktop span is a child of the <button>, so a pointer event anywhere in
// that region is an event inside the button and runs its onClick; being
// absolutely positioned it is not a flex item, so `.btn`'s `gap` and
// `align-items` never see it and it shifts no pixel. It is aria-hidden, carries
// no handler and is not focusable — it enlarges what is CLICKABLE and nothing
// else. Visual weight, resting and hovered, is unchanged from Settings.
//
// KEYBOARD. Exactly one focusable node in either branch — the <button> — with
// no interactive element nested inside it. The ring comes from
// `.btn:focus-visible` at >= 1280 and from `.mv2-x-hit:focus-visible > span`
// below it; both are the same 2px `--mod-accent-line` inset ring the module's
// ChromeNavV4 controls use.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import useTranslation from "../../../../../shared/hooks/useTranslation";
import { V2_BELOW_DESKTOP, useV2MediaQuery } from "../../useV2MediaQuery";

/** How far the DESKTOP branch's transparent hit-area child is pushed past each
 *  edge of the button's 28x28 PADDING box — the containing block an absolutely
 *  positioned child resolves against — to reach the 44px target the manager
 *  requires: 28 + 8 + 8 = 44. It is NOT half of (44 - 30); see this file's
 *  header for why a 30px `.btn` border box measures 28 inside. The below-1280
 *  branch needs no such child: there the button IS the 44px box. */
const HIT_INSET = -8;

export interface WallExitButtonProps {
  /** THE wall's one exit — FleetPage's `exitWall`, the very same callback Esc
   *  runs (FleetWallView's keydown effect). Two triggers, one implementation;
   *  there is no second exit path here that could drift from it. */
  onExit: () => void;
}

const WallExitButton: React.FC<WallExitButtonProps> = ({ onExit }) => {
  const { translate } = useTranslation("myMachinery");
  // Read HERE rather than threaded from the two hosts: a prop a caller can
  // forget is the shape the deleted opt-out class had, and this control has
  // three render sites. One matchMedia listener, on the module's one predicate.
  const belowDesktop = useV2MediaQuery(V2_BELOW_DESKTOP);

  // A real name, not the bare glyph: a screen reader announcing "times, button"
  // on a kiosk tells nobody anything. The wording is the module's own — the
  // pointer-revealed chip this control replaces carried the same phrase behind
  // a keyboard prefix ("Esc — back to overview" / "Esc — späť na prehľad").
  // That key was retired with the chip; the phrase moved onto the control it
  // names, minus a prefix that no longer describes the affordance (en "Back to
  // overview" / sk "Späť na prehľad" — both re-read in
  // public/locales/{en,sk}/myMachinery.json at commit time).
  const label = translate("myMachinery:v2.fleet.wall.exitLabel");

  if (belowDesktop) {
    return (
      <button type="button" className="mv2-x-hit" aria-label={label} onClick={onExit}>
        {/* Unclassed on purpose: the module-wide floor matches ANY `.btn`, so a
            painted child carrying it would inflate to 44 exactly as its parent
            used to. Its whole paint is `.mv2-x-hit > span`. */}
        <span>×</span>
      </button>
    );
  }

  return (
    <button
      type="button"
      className="btn btn-ghost"
      aria-label={label}
      onClick={onExit}
      style={{
        // Settings' four values (SettingsV2DialogShell.tsx:226-235), unchanged.
        // Then three this placement adds:
        //   height: 30 — REDUNDANT, not protective. `.btn` already declares
        //     `height: 30px` (tokens :166) and nothing stretches it: the header
        //     is `alignItems: "center"`, never `stretch`, and this button is no
        //     longer even a flex item of it (WallHeader pins it absolutely). It
        //     is kept only so both axes of the painted box are stated beside
        //     the HIT_INSET arithmetic, which needs both to be 30.
        //   justifyContent — `.btn` is `display: inline-flex` (tokens :174) at
        //     the default `flex-start`, so with `padding: 0` in an explicit
        //     30px box the glyph would otherwise sit flush against the left
        //     edge.
        //   position: relative — the anchor for the hit-area child below.
        width: 30,
        height: 30,
        padding: 0,
        fontSize: 15,
        color: "var(--muted)",
        justifyContent: "center",
        position: "relative",
      }}
    >
      ×
      <span
        aria-hidden
        style={{
          position: "absolute",
          top: HIT_INSET,
          right: HIT_INSET,
          bottom: HIT_INSET,
          left: HIT_INSET,
        }}
      />
    </button>
  );
};

export default WallExitButton;
